import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Input, Spinner } from 'src/UIComponents';
import { updateSomething, removeSomething } from 'src/api-services';
import { useSomething } from 'src/queries/something';

const ActionsContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

function SomethingEdit({ id, ...rest }) {
  const titleRef = useRef(null);
  const queryClient = useQueryClient();
  const [newTitle, setTitle] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const { isLoading, data: something } = useSomething(id);
  const { mutate: update, isLoading: isSaving } = useMutation(updateSomething, {
    onSuccess: ({ data: createdItem }) => {
      setTitle('');
      navigate(-1);
    },
  });
  const { mutate: mutateDelete, isLoading: deleting } = useMutation(
    removeSomething,
    {
      onSuccess: ({ data: removedItem }) => {
        const removedId = Number.parseInt(removedItem.id, 10);
        queryClient.setQueryData('anythings', (cachedQuery) => {
          return {
            ...cachedQuery,
            data: cachedQuery?.data?.filter((item) => item.id !== removedId),
          };
        });
        navigate('/somethings');
      },
    }
  );

  useEffect(() => {
    titleRef.current && titleRef.current.focus();
  }, [something.title]);

  useEffect(() => {
    setTitle(something.title || '');
  }, [something.title]);

  const toggleConfirmDelete = useCallback(
    () => setConfirmOpen((prevState) => !prevState),
    []
  );
  const deleteItem = useCallback(() => {
    if (deleting) {
      return;
    }
    mutateDelete(id);
  }, [mutateDelete, deleting, id]);

  const onChangeTitle = useCallback((event) => {
    setTitle(event.target.value);
  }, []);

  const goBack = useCallback(() => navigate(-1), [navigate]);

  const doSave = useCallback(() => {
    if (isSaving) {
      return;
    }
    update({
      id,
      title: newTitle,
    });
  }, [update, isSaving, id, newTitle]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div {...rest}>
      <div className="row">
        <div className="six columns">
          <Input
            ref={titleRef}
            value={newTitle}
            disabled={isLoading}
            onChange={onChangeTitle}
            style={{
              width: '100%',
            }}
          />
        </div>
      </div>

      <ActionsContainer>
        {confirmOpen ? (
          <>
            <Button $color="danger" onClick={deleteItem}>
              Confirm delete
            </Button>
            <Button $color="secondary" onClick={toggleConfirmDelete}>
              Abort
            </Button>
          </>
        ) : (
          <>
            <Button onClick={doSave} $color="primary" disabled={isLoading}>
              {isSaving ? <Spinner size="1em" /> : 'Save'}
            </Button>
            <Button disabled={isSaving} onClick={goBack}>
              Cancel
            </Button>
            <Button $color="danger" outline onClick={toggleConfirmDelete}>
              Delete
            </Button>
          </>
        )}
      </ActionsContainer>
    </div>
  );
}

SomethingEdit.defaultProps = {};

SomethingEdit.propTypes = {};

export default SomethingEdit;
